/**
 * VAT Calculation Helper
 *
 * Centralized utility for VAT calculations in invoice forms.
 * Ensures consistency between frontend calculations and backend logic.
 *
 * @module vatCalculations
 */

/**
 * Result of VAT calculation containing all monetary amounts
 */
export interface VatCalculationResult {
  /** Net amount (base price without VAT) */
  netAmount: number;
  /** VAT rate as percentage (e.g., 23 for 23%) */
  vatRate: number;
  /** Calculated VAT amount */
  vatAmount: number;
  /** Total amount including VAT (net + vat) */
  grossAmount: number;
}

/**
 * Configuration for VAT calculations
 */
export interface VatCalculationConfig {
  /** Number of decimal places for rounding (default: 2) */
  decimalPlaces?: number;
  /** Whether to validate input values (default: true) */
  validate?: boolean;
}

/**
 * Default configuration for VAT calculations
 */
const DEFAULT_CONFIG: Required<VatCalculationConfig> = {
  decimalPlaces: 2,
  validate: true,
};

/**
 * Validates VAT calculation input parameters
 *
 * @param amount - Amount to validate
 * @param vatRate - VAT rate to validate
 * @param amountLabel - Label for error messages
 * @throws {Error} If validation fails
 */
const validateInput = (amount: number, vatRate: number, amountLabel: string = 'Amount'): void => {
  if (typeof amount !== 'number' || isNaN(amount)) {
    throw new Error(`${amountLabel} must be a valid number`);
  }

  if (amount < 0) {
    throw new Error(`${amountLabel} cannot be negative`);
  }

  if (typeof vatRate !== 'number' || isNaN(vatRate)) {
    throw new Error('VAT rate must be a valid number');
  }

  if (vatRate < 0 || vatRate > 100) {
    throw new Error('VAT rate must be between 0 and 100');
  }
};

/**
 * Rounds a number to specified decimal places
 *
 * @param value - Value to round
 * @param decimalPlaces - Number of decimal places
 * @returns Rounded value
 */
const roundToDecimal = (value: number, decimalPlaces: number): number => {
  return parseFloat(value.toFixed(decimalPlaces));
};

/**
 * Calculates VAT amount and gross total from net amount
 *
 * @example
 * ```typescript
 * const result = calculateVat(100, 23);
 * // result = { netAmount: 100, vatRate: 23, vatAmount: 23, grossAmount: 123 }
 * ```
 *
 * @param netAmount - Base price without VAT
 * @param vatRate - VAT rate as percentage (e.g., 23 for 23%)
 * @param config - Optional configuration
 * @returns Calculation result with all amounts
 * @throws {Error} If input validation fails
 */
export const calculateVat = (
  netAmount: number,
  vatRate: number,
  config: VatCalculationConfig = {}
): VatCalculationResult => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  if (finalConfig.validate) {
    validateInput(netAmount, vatRate, 'Net amount');
  }

  const vatAmount = roundToDecimal(netAmount * (vatRate / 100), finalConfig.decimalPlaces);

  const grossAmount = roundToDecimal(netAmount + vatAmount, finalConfig.decimalPlaces);

  return {
    netAmount: roundToDecimal(netAmount, finalConfig.decimalPlaces),
    vatRate,
    vatAmount,
    grossAmount,
  };
};

/**
 * Calculates net amount and VAT from gross total
 *
 * Useful when user enters price including VAT and needs to extract net amount.
 *
 * @example
 * ```typescript
 * const result = calculateNetFromGross(123, 23);
 * // result = { netAmount: 100, vatRate: 23, vatAmount: 23, grossAmount: 123 }
 * ```
 *
 * @param grossAmount - Total price including VAT
 * @param vatRate - VAT rate as percentage (e.g., 23 for 23%)
 * @param config - Optional configuration
 * @returns Calculation result with all amounts
 * @throws {Error} If input validation fails
 */
export const calculateNetFromGross = (
  grossAmount: number,
  vatRate: number,
  config: VatCalculationConfig = {}
): VatCalculationResult => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  if (finalConfig.validate) {
    validateInput(grossAmount, vatRate, 'Gross amount');
  }

  const divisor = 1 + vatRate / 100;
  const netAmount = roundToDecimal(grossAmount / divisor, finalConfig.decimalPlaces);

  const vatAmount = roundToDecimal(grossAmount - netAmount, finalConfig.decimalPlaces);

  return {
    netAmount,
    vatRate,
    vatAmount,
    grossAmount: roundToDecimal(grossAmount, finalConfig.decimalPlaces),
  };
};

/**
 * Calculates total amounts for multiple invoice items
 *
 * @example
 * ```typescript
 * const items = [
 *   { netAmount: 100, vatRate: 23 },
 *   { netAmount: 50, vatRate: 8 }
 * ];
 * const total = calculateInvoiceTotal(items);
 * // total = { netAmount: 150, vatAmount: 27, grossAmount: 177 }
 * ```
 *
 * @param items - Array of items with net amount and VAT rate
 * @param config - Optional configuration
 * @returns Total amounts for the invoice
 */
export const calculateInvoiceTotal = (
  items: Array<{ netAmount: number; vatRate: number }>,
  config: VatCalculationConfig = {}
): Omit<VatCalculationResult, 'vatRate'> => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  if (!Array.isArray(items)) {
    throw new Error('Items must be an array');
  }

  if (items.length === 0) {
    return {
      netAmount: 0,
      vatAmount: 0,
      grossAmount: 0,
    };
  }

  const totals = items.reduce(
    (acc, item) => {
      if (finalConfig.validate) {
        validateInput(item.netAmount, item.vatRate, 'Item net amount');
      }

      const itemCalc = calculateVat(item.netAmount, item.vatRate, {
        ...config,
        validate: false, // Already validated above
      });

      return {
        netAmount: acc.netAmount + itemCalc.netAmount,
        vatAmount: acc.vatAmount + itemCalc.vatAmount,
        grossAmount: acc.grossAmount + itemCalc.grossAmount,
      };
    },
    { netAmount: 0, vatAmount: 0, grossAmount: 0 }
  );

  return {
    netAmount: roundToDecimal(totals.netAmount, finalConfig.decimalPlaces),
    vatAmount: roundToDecimal(totals.vatAmount, finalConfig.decimalPlaces),
    grossAmount: roundToDecimal(totals.grossAmount, finalConfig.decimalPlaces),
  };
};

/**
 * Calculates unit price from total amount and quantity
 *
 * @example
 * ```typescript
 * const unitPrice = calculateUnitPrice(246, 2, 23);
 * // unitPrice = 100 (net per unit)
 * ```
 *
 * @param totalGrossAmount - Total gross amount for all units
 * @param quantity - Number of units
 * @param vatRate - VAT rate as percentage
 * @param config - Optional configuration
 * @returns Net unit price
 * @throws {Error} If quantity is zero or validation fails
 */
export const calculateUnitPrice = (
  totalGrossAmount: number,
  quantity: number,
  vatRate: number,
  config: VatCalculationConfig = {}
): number => {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };

  if (finalConfig.validate) {
    if (quantity <= 0) {
      throw new Error('Quantity must be greater than zero');
    }
    validateInput(totalGrossAmount, vatRate, 'Total gross amount');
  }

  const grossPerUnit = totalGrossAmount / quantity;
  const result = calculateNetFromGross(grossPerUnit, vatRate, config);

  return result.netAmount;
};

/**
 * Validates if calculated amounts match within acceptable tolerance
 *
 * Useful for comparing frontend calculations with backend responses.
 *
 * @example
 * ```typescript
 * const isValid = validateCalculation(
 *   { netAmount: 100, vatAmount: 23, grossAmount: 123 },
 *   { netAmount: 100.01, vatAmount: 23.00, grossAmount: 123.01 },
 *   0.02
 * );
 * // isValid = true (differences within 0.02 tolerance)
 * ```
 *
 * @param expected - Expected amounts
 * @param actual - Actual amounts to compare
 * @param tolerance - Maximum acceptable difference (default: 0.01)
 * @returns True if all amounts match within tolerance
 */
export const validateCalculation = (
  expected: Omit<VatCalculationResult, 'vatRate'>,
  actual: Omit<VatCalculationResult, 'vatRate'>,
  tolerance: number = 0.01
): boolean => {
  const isWithinTolerance = (a: number, b: number): boolean => {
    return Math.abs(a - b) <= tolerance;
  };

  return (
    isWithinTolerance(expected.netAmount, actual.netAmount) &&
    isWithinTolerance(expected.vatAmount, actual.vatAmount) &&
    isWithinTolerance(expected.grossAmount, actual.grossAmount)
  );
};

/**
 * Formats amount for display with currency
 *
 * @example
 * ```typescript
 * const formatted = formatAmount(123.456, 'PLN');
 * // formatted = "123.46 PLN"
 * ```
 *
 * @param amount - Amount to format
 * @param currency - Currency code (default: 'PLN')
 * @param decimalPlaces - Number of decimal places (default: 2)
 * @returns Formatted string
 */
export const formatAmount = (
  amount: number,
  currency: string = 'PLN',
  decimalPlaces: number = 2
): string => {
  return `${amount.toFixed(decimalPlaces)} ${currency}`;
};
